import {
  Link,
  useNavigate
} from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData
} from "@whitespace/whitespace-ui";
import { DateRangePickerComponent } from "@syncfusion/ej2-react-calendars";
import { useCallback, useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import IAddNonWorkingPeriodRequestModel from "interfaces/Request/IAddNonWorkingPeriodRequestModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import nonWorkingPeriodSchema from "modules/schemas/nonWorkingPeriodSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { makeDateRangePickerAACompliant, makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import { format } from "date-fns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IDisposalSite from "interfaces/IDisposalSite";
import IGetSeveritiesResponseModel from "interfaces/Response/IGetSeveritiesResponseModel";

import "styles/sf-base.scss";
import "styles/sf-calendar.scss";
import "styles/sf-dropdown.scss";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddNonWorkingPeriod = () => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddNonWorkingPeriodRequestModel>({
    resolver: yupResolver(nonWorkingPeriodSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: getSeveritiesIsSending,
    errors: getSeveritiesErrors,
    data: getSeveritiesData,
    sendRequest: getSeveritiesSendRequest
  } = useAmplifyREST();

  const {
    isSending: getDisposalSitesIsSending,
    errors: getDisposalSitesErrors,
    data: getDisposalSitesData,
    sendRequest: getDisposalSitesSendRequest
  } = useAmplifyREST();

  const {
    isSending: addNonWorkingPeriodIsSending,
    errors: addNonWorkingPeriodErrors,
    statusCode: addNonWorkingPeriodStatusCode,
    sendRequest: addNonWorkingPeriodSendRequest
  } = useAmplifyREST();

  const [aaFixDateRangePickerCompleted, setAAFixDateRangePickerCompleted] = useState<boolean>(false);
  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);
  const isSending = getSeveritiesIsSending || getDisposalSitesIsSending || addNonWorkingPeriodIsSending
                      || !aaFixDateRangePickerCompleted || !aaFixDropDownTreeCompleted;
  const apiErrors = getSeveritiesErrors || getDisposalSitesErrors || addNonWorkingPeriodErrors;
  const navigate = useNavigate();

  let dateRangePickerComponent : DateRangePickerComponent | undefined;
  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();

  const [selectedDateFrom, setSelectedDateFrom] = useState<Date|undefined>();
  const [selectedDateTo, setSelectedDateTo] = useState<Date|undefined>();
  const [selectedDisposalSites, setSelectedDisposalSites] = useState<string[]|undefined>();

  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [disposalSites, setDisposalSites] = useState<IDisposalSite[]|undefined>();

  const disposalSiteFields : Object = {
    dataSource: disposalSites, value: "id", text: "name", child: "nodeChild"
  };

  const disposalSiteTreeSettings = { autoCheck: true };

  const canFormBeSubmitted = () => {
    setAddButtonDisabled(
      !(
        getValues("name").length > 0
        && selectedDateFrom
        && selectedDateTo
        && getValues("description").length > 0
        && getValues("severity") !== ""
        && selectedDisposalSites && selectedDisposalSites.length > 0
      )
    );
  };

  const datePickerOnChange = (props : any) => {
    // eslint-disable-next-line react/prop-types
    setSelectedDateFrom(props.startDate ?? undefined);
    // eslint-disable-next-line react/prop-types
    setSelectedDateTo(props.endDate ?? undefined);
  };

  const dropDownTreeComponentOnChange = () => {
    setSelectedDisposalSites(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedDateFrom, selectedDateTo, selectedDisposalSites]);

  useEffect(() => {
    getSeveritiesSendRequest("settings/nonworkingperiods/severities", "GET");
  }, [getSeveritiesSendRequest]);

  useEffect(() => {
    getDisposalSitesSendRequest("settings/disposalsites", "GET");
  }, [getDisposalSitesSendRequest]);

  useEffect(() => {
    if (getDisposalSitesData) {
      setDisposalSites(getDisposalSitesData.disposalSites);
    }
  }, [getDisposalSitesData]);

  const handleAddNonWorkingPeriodClick = (request: IAddNonWorkingPeriodRequestModel) => {
    request.dateFrom = format(selectedDateFrom!, "yyyy-MM-dd");
    request.dateTo = format(selectedDateTo!, "yyyy-MM-dd");
    request.disposalSites = selectedDisposalSites!;
    addNonWorkingPeriodSendRequest("settings/nonworkingperiods", "POST", request);
  };

  useEffect(() => {
    if (addNonWorkingPeriodStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsNonWorkingPeriods);
    }
  }, [addNonWorkingPeriodStatusCode]);

  useEffect(() => {
    if (dateRangePickerComponent) {
      makeDateRangePickerAACompliant(true);
      setAAFixDateRangePickerCompleted(true);
    }
  }, [dateRangePickerComponent]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  const renderSeverities = () => {
    const regions = [
      <option key="" value="">Please Select</option>
    ];
    if (getSeveritiesData) {
      (getSeveritiesData as IGetSeveritiesResponseModel).severities.forEach(severity => {
        regions.push(<option key={severity} value={severity}>{severity}</option>);
      });
    }

    return regions;
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-non-working-period">
              Create Non-Working Period
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleAddNonWorkingPeriodClick)}>
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-nonworkingperiods"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsNonWorkingPeriods }}
                    onClick={() => navigate(RoutePathsEnum.SettingsNonWorkingPeriods)}

                  >
                    Non-Working Periods
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    active
                    data-testid="breadcrumb-addnonworkingperiod"
                  >
                    Add New Non-Working Period
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || addNonWorkingPeriodIsSending}
                  aria-disabled={addButtonDisabled || addNonWorkingPeriodIsSending}
                  aria-label="Create non-working period"
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsNonWorkingPeriods)}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to non-working periods"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="pt-2" data-testid="formElements">
              <Container fluid>
                <Row className="d-flex mt-1 ms-1">
                  <Col xs={12}>
                    <div className="control-pane">
                      <div className="control-section" />
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="name">
                          <Form.Label id="nameLabel" className="horiz-form-label">
                            Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck("name", errors, null)}
                              aria-labelledby="nameLabel"
                              maxLength={50}
                              onKeyUp={() => canFormBeSubmitted()}
                              {...register("name")}
                            />
                          </Col>
                          <ErrorMessages fieldName="name" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="code">
                          <Form.Label id="dateRangeLabel" className="horiz-form-label">
                            Date Range:
                          </Form.Label>
                          <Col>
                            <DateRangePickerComponent
                              change={datePickerOnChange}
                              ref={(d:DateRangePickerComponent) => { dateRangePickerComponent = d; }}
                              strictMode
                              id="datepicker"
                              format="dd/MM/yyyy"
                              htmlAttributes={{
                                "aria-labelledby": "dateRangeLabel",
                                "aria-required": "true"
                              }}
                            />
                          </Col>
                          <ErrorMessages fieldName="dateRange" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="description">
                          <Form.Label id="descriptionLabel" className="horiz-form-label">
                            Description:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck("name", errors, null)}
                              aria-labelledby="descriptionLabel"
                              maxLength={50}
                              onKeyUp={() => canFormBeSubmitted()}
                              {...register("description")}
                            />
                          </Col>
                          <ErrorMessages fieldName="description" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="severity">
                          <Form.Label id="severityLabel" className="horiz-form-label">
                            Severity:
                          </Form.Label>
                          <Col>
                            <Form.Select
                              {...register("severity", { onChange: () => canFormBeSubmitted() })}
                              data-testid="ddlSeverity"
                              aria-required
                              aria-invalid={false}
                              aria-labelledby="severityLabel"
                            >
                              {renderSeverities()}
                            </Form.Select>
                            <NoData visible={!apiErrors && !getSeveritiesIsSending && !getSeveritiesData} description="No Severities Found" />

                          </Col>
                          <ErrorMessages fieldName="severity" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="disposalSites">
                          <Form.Label id="disposalSitesLabel" className="horiz-form-label">
                            Disposal Sites:
                          </Form.Label>
                          <Col>
                            {disposalSites && (
                              <DropDownTreeComponent
                                {...register("disposalSites")}
                                ref={(d:DropDownTreeComponent) => { setDropDownTreeComponent(d); }}
                                change={dropDownTreeComponentOnChange}
                                treeSettings={disposalSiteTreeSettings}
                                fields={disposalSiteFields}
                                allowMultiSelection
                                showCheckBox
                                changeOnBlur={false}
                                mode="Box"
                                id="disposalSitesDropDown"
                                htmlAttributes={{
                                  "data-testid": "ddlDisposalSites",
                                  "aria-required": "true"
                                }}
                              />
                            )}
                            <NoData visible={!apiErrors && !getDisposalSitesIsSending && !disposalSites} description="No Disposal Sites Found" />
                          </Col>
                          <ErrorMessages fieldName="disposalSites" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2 mt-3">
                        <Form.Group as={Row} controlId="active" className="d-flex align-items-center">
                          <Form.Label id="activeLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Active:
                          </Form.Label>
                          <Form.Check
                            {...register("active")}
                            type="switch"
                            className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                            aria-required="true"
                            aria-labelledby="activeLabel"
                            defaultChecked
                            onKeyUp={() => canFormBeSubmitted()}
                          />
                          <ErrorMessages fieldName="active" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                    </div>
                  </Col>
                </Row>

              </Container>
            </section>
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default AddNonWorkingPeriod;
